// src/components/RoomCard.jsx

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardMedia, Typography, Box } from "@mui/material";
import { useSelector } from "react-redux";
import { getRoomFields } from "../services/roomFieldService";
import { Image as ImageIcon } from "@mui/icons-material";

function RoomCard({ room }) {
  const [roomFields, setRoomFields] = useState([]);
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  
  // Get user from Redux store
  const user = useSelector((state) => state.user.user);

  useEffect(() => {
    // Only load fields if user is authenticated
    if (user) {
      loadRoomFields();
    } else {
      setFieldsLoaded(true); // Don't try to load if not authenticated
    }
  }, [user]);

  // Debug: Log room data to see if images are present
  useEffect(() => {
    console.log('RoomCard - Room data:', room);
    if (room.images) {
      console.log('RoomCard - Images found:', room.images);
    } else {
      console.log('RoomCard - No images found in room data');
    }
  }, [room]);

  const loadRoomFields = async () => {
    try {
      const fields = await getRoomFields();
      setRoomFields(fields || []);
    } catch (error) {
      console.error('Error loading room fields for display:', error);
    } finally {
      setFieldsLoaded(true);
    }
  };

  const renderFieldValue = (field, value) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    switch (field.type) {
      case 'boolean':
        return value === true || value === 'true' ? 'Yes' : 'No';
      case 'select':
        return value;
      case 'number':
        return value.toString();
      default:
        return value;
    }
  };

  const renderRoomFields = () => {
    if (!fieldsLoaded || !roomFields.length) {
      // Fallback for when no fields are configured
      return (
        <Box>
          {room.capacity && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Capacity:</strong> {room.capacity}
            </Typography>
          )}
          {room.location && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Location:</strong> {room.location}
            </Typography>
          )}
        </Box>
      );
    }

    return roomFields
      .sort((a, b) => a.order - b.order)
      .map(field => {
        let value;
        
        // Handle system fields
        if (field.isSystem) {
          if (field.name === 'location') {
            value = room.location;
          } else if (field.name === 'capacity') {
            value = room.capacity;
          }
        } else {
          // Handle custom fields
          const customFields = room.customFields instanceof Map 
            ? Object.fromEntries(room.customFields)
            : room.customFields;
          value = customFields?.[field.name];
        }

        const displayValue = renderFieldValue(field, value);
        
        if (!displayValue) return null;

        return (
          <Typography key={field._id} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <strong>{field.label}:</strong> {displayValue}
          </Typography>
        );
      })
      .filter(Boolean); // Remove null values
  };

  // Get the first image from the room's images array
  const roomImage = room.images && room.images.length > 0 ? room.images[0] : null;

  return (
    <Card sx={{ mb: 2, overflow: 'hidden' }}>
      {/* Room Image */}
      {roomImage ? (
        <CardMedia
          component="img"
          height="240"
          image={roomImage.url || roomImage.thumbnailUrl}
          alt={`${room.name} image`}
          sx={{
            objectFit: 'cover',
            backgroundColor: 'grey.100',
            imageRendering: 'high-quality',
            filter: 'contrast(1.02) saturate(1.05)',
            transition: 'transform 0.3s ease, filter 0.3s ease',
            '&:hover': {
              transform: 'scale(1.02)',
              filter: 'contrast(1.05) saturate(1.1)'
            }
          }}
          onLoad={(e) => {
            // Ensure crisp image rendering
            e.target.style.imageRendering = 'auto';
          }}
        />
      ) : (
        <Box
          sx={{
            height: 200,
            backgroundColor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'grey.400'
          }}
        >
          <ImageIcon sx={{ fontSize: 48 }} />
        </Box>
      )}
      
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>{room.name}</Typography>
        
        {/* Render all fields in configured order */}
        {renderRoomFields()}
      </CardContent>
    </Card>
  );
}

export default RoomCard;
